import {useCallback, useMemo, useRef, useState, type Dispatch, type ReactElement, type SetStateAction} from "react";
import {ListCardComponent, type ListCardCategoryDisplay, type ListCardFooterControls, type ListCardFooterActionsPreset, type ListCardTimelinessEmphasis} from "~/components/ottopilotDataViewer/listCardComponent";
import {resolveListFunnelProgress} from "~/components/ottopilotDataViewer/listFunnel";
import type {ListCardLozengeStyle, ListCardStatus} from "~/components/ottopilotDataViewer/listStatusConfig";
import {ListCardRowActions} from "~/components/ottopilotDataViewer/rowEngagement/ListCardRowActions";
import type {TableRowActionsConfig} from "~/components/table/tableRowActions.types";
import {
    ViewType,
    type OttopilotListCardRenderContext,
    type OttopilotView,
} from "~/components/ottopilotDataViewer/view";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";

export type OttopilotListCardFieldsMapper<T> = {
    getSubtitle?: (row: T) => string | null;
    getDisplayName: (row: T) => string;
    getPhoneNumber: (row: T) => string | null;
    getStatus: (row: T) => ListCardStatus;
    getCreatedAt: (row: T) => Iso8601DateTime;
    getAssignedOuName: (row: T) => string | null;
    getAssignedOuColumnLabel?: (row: T) => string;
    /** When set, list cards show phone and UTM source under the display name. */
    getUtmSource?: (row: T) => string | null;
};

export type OttopilotListCardFooterBindings = {
    getFooterControls: (footerCorrelationId: Uuid) => ListCardFooterControls;
    selectedDispositionId: Uuid | null;
    setSelectedDispositoinId: Dispatch<SetStateAction<Uuid | null>>;
    /** Event Container to prefill when opening the list-card create sheet (same as `?eventContainerId=` from its details page). */
    selectedCreateJourneyId: Uuid | null;
    setSelectedCreateJourneyId: Dispatch<SetStateAction<Uuid | null>>;
    selectedRemarks: string | null;
    setSelectedRemarks: Dispatch<SetStateAction<string | null>>;
    selectedSelectedNotes: string | null;
    setSelectedSelectedNotes: Dispatch<SetStateAction<string | null>>;
};

/**
 * Shared footer UI state for {@link ListCardComponent} (disposition + remarks + notes).
 * Mount list-specific modals/drawers beside {@link OttopilotDataViewer} via controller.listCardChrome.
 */
export function useOttopilotListCardFooterBindings(): OttopilotListCardFooterBindings {
    const [selectedDispositionId, setSelectedDispositoinId] = useState<Uuid | null>(null);
    const [selectedCreateJourneyId, setSelectedCreateJourneyId] = useState<Uuid | null>(null);
    const [selectedRemarks, setSelectedRemarks] = useState<string | null>(null);
    const [selectedSelectedNotes, setSelectedSelectedNotes] = useState<string | null>(null);

    const getFooterControls = useCallback(
        (footerCorrelationId: Uuid): ListCardFooterControls => ({
            selectedDispositionId,
            setSelectedDispositoinId,
            selectedRemarks,
            setSelectedRemarks,
            selectedSelectedNotes,
            setSelectedSelectedNotes,
            journeyId: footerCorrelationId,
        }),
        [selectedDispositionId, selectedRemarks, selectedSelectedNotes],
    );

    return useMemo(
        () => ({
            getFooterControls,
            selectedDispositionId,
            setSelectedDispositoinId,
            selectedCreateJourneyId,
            setSelectedCreateJourneyId,
            selectedRemarks,
            setSelectedRemarks,
            selectedSelectedNotes,
            setSelectedSelectedNotes,
        }),
        [
            getFooterControls,
            selectedDispositionId,
            setSelectedDispositoinId,
            selectedCreateJourneyId,
            setSelectedCreateJourneyId,
            selectedRemarks,
            setSelectedRemarks,
            selectedSelectedNotes,
            setSelectedSelectedNotes,
        ],
    );
}

export type OttopilotListCardFunnelOptions<T> = {
    stageColumnIds: readonly Uuid[];
    getAdditionalColumnValues: (row: T) => Record<Uuid, unknown>;
};

export type OttopilotListCardDisplayExtras<T> = {
    statusColumnLabel?: string;
    timelinessColumnLabel?: string;
    getTimelinessDisplay?: (row: T) => string;
    getTimelinessEmphasis?: (row: T) => ListCardTimelinessEmphasis;
    /** When set, used for the timeliness line on mobile list cards instead of {@link getTimelinessDisplay}. */
    getMobileTimelinessDisplay?: (row: T) => string;
    statusDisplayAsText?: boolean;
    footerActionsPreset?: ListCardFooterActionsPreset;
    /** When true, hides the assigned OU / State column on desktop list cards. */
    hideAssignedOuColumn?: boolean;
    /** When true, hides the status column on desktop list cards. */
    hideStatusColumn?: boolean;
    categoryColumnLabel?: string;
    getCategoryDisplay?: (row: T) => ListCardCategoryDisplay | null;
    /** When set, assigned-OU / disposition column value renders as a lozenge. */
    getAssignedOuLozengeStyle?: (row: T) => ListCardLozengeStyle | null;
    /** When set, list card footer uses the same actions as the table row actions column. */
    tableRowActionsConfig?: TableRowActionsConfig<T>;
};

export type OttopilotListCardPresentationOptions = {
    /** When true, hides the assigned OU / State column on desktop list cards by default. */
    hideAssignedOuColumn?: boolean;
};

export type OttopilotListCardActionHandlers<T> = {
    onPhoneClick: (row: T, bindings: OttopilotListCardFooterBindings) => void;
    onScheduleClick?: (row: T, footerCorrelationId: Uuid, bindings: OttopilotListCardFooterBindings) => void;
    onDisposeClick?: (row: T, footerCorrelationId: Uuid, bindings: OttopilotListCardFooterBindings) => void;
    onCreateClick?: (row: T, footerCorrelationId: Uuid, bindings: OttopilotListCardFooterBindings) => void;
    onNotesClick?: (row: T, footerCorrelationId: Uuid, bindings: OttopilotListCardFooterBindings) => void;
    onEditClick?: (row: T, footerCorrelationId: Uuid, bindings: OttopilotListCardFooterBindings) => void;
    onLogCallClick?: (row: T, footerCorrelationId: Uuid, bindings: OttopilotListCardFooterBindings) => void;
    onProfileClick?: (row: T, footerCorrelationId: Uuid, bindings: OttopilotListCardFooterBindings) => void;
    onWhatsAppClick?: (row: T, footerCorrelationId: Uuid, bindings: OttopilotListCardFooterBindings) => void;
    /** When true, schedule/dispose/create/notes footer actions are disabled for this row. */
    isListCardFooterRestricted?: (row: T) => boolean;
    /** When true, only the schedule footer action is disabled for this row. */
    isScheduleClickDisabled?: (row: T) => boolean;
    /** When true, WhatsApp is hidden/disabled in the day-plan More menu. */
    isWhatsAppClickDisabled?: (row: T) => boolean;
    resolveListCardDisplayExtras?: (row: T) => OttopilotListCardDisplayExtras<T>;
};

/**
 * Generic list-card controller: maps rows to {@link ListCardComponent} and wires footer bindings.
 * Uses refs for field/action mappers so callers are not forced to memoize object identities.
 */
export function useOttopilotListCardPresentation<T extends {id: Uuid}>(
    fields: OttopilotListCardFieldsMapper<T>,
    resolveFooterControls: (row: T, bindings: OttopilotListCardFooterBindings) => ListCardFooterControls | null,
    handlers: OttopilotListCardActionHandlers<T>,
    funnelOptions?: OttopilotListCardFunnelOptions<T>,
    sharedFooterBindings?: OttopilotListCardFooterBindings,
    presentationOptions?: OttopilotListCardPresentationOptions,
): {
    renderListCard: (row: T, listCardContext?: OttopilotListCardRenderContext) => ReactElement;
    footerBindings: OttopilotListCardFooterBindings;
} {
    const internalFooterBindings = useOttopilotListCardFooterBindings();
    const footerBindings = sharedFooterBindings ?? internalFooterBindings;

    const fieldsRef = useRef(fields);
    fieldsRef.current = fields;

    const resolveFooterRef = useRef(resolveFooterControls);
    resolveFooterRef.current = resolveFooterControls;

    const handlersRef = useRef(handlers);
    handlersRef.current = handlers;

    const funnelOptionsRef = useRef(funnelOptions);
    funnelOptionsRef.current = funnelOptions;

    const presentationOptionsRef = useRef(presentationOptions);
    presentationOptionsRef.current = presentationOptions;

    const renderListCard = useCallback(
        (row: T, listCardContext?: OttopilotListCardRenderContext) => {
            const f = fieldsRef.current;
            const h = handlersRef.current;
            const footerControls = resolveFooterRef.current(row, footerBindings);
            const funnel = funnelOptionsRef.current;
            const funnelProgress =
                funnel === undefined
                    ? undefined
                    : resolveListFunnelProgress(
                          funnel.getAdditionalColumnValues(row),
                          funnel.stageColumnIds,
                      );
            const footerActionsRestricted = h.isListCardFooterRestricted?.(row) === true;
            const scheduleClickDisabled = h.isScheduleClickDisabled?.(row) === true;
            const whatsAppActionDisabled = h.isWhatsAppClickDisabled?.(row) === true;
            const displayExtras = h.resolveListCardDisplayExtras?.(row);
            const hideAssignedOuColumn =
                displayExtras?.hideAssignedOuColumn ?? presentationOptionsRef.current?.hideAssignedOuColumn;
            const tableRowActionsConfig = displayExtras?.tableRowActionsConfig;
            const footerActionsRenderer =
                tableRowActionsConfig !== undefined
                    ? () => (
                          <ListCardRowActions
                              row={row}
                              config={tableRowActionsConfig}
                              layout={listCardContext?.layout ?? "mobile"}
                              // Row-actions-only cards (no disposition footer) use the stacked labeled bar.
                              stacked={footerControls === null}
                          />
                      )
                    : undefined;

            return (
                <ListCardComponent
                    diplayName={f.getDisplayName(row)}
                    subtitle={f.getSubtitle?.(row)}
                    phoneNumber={f.getPhoneNumber(row)}
                    status={f.getStatus(row)}
                    createdAt={f.getCreatedAt(row)}
                    assignedOuName={f.getAssignedOuName(row)}
                    assignedOuColumnLabel={f.getAssignedOuColumnLabel?.(row)}
                    utmSource={f.getUtmSource === undefined ? undefined : f.getUtmSource(row)}
                    statusColumnLabel={displayExtras?.statusColumnLabel}
                    timelinessColumnLabel={displayExtras?.timelinessColumnLabel}
                    timelinessDisplayValue={displayExtras?.getTimelinessDisplay?.(row)}
                    mobileTimelinessDisplayValue={displayExtras?.getMobileTimelinessDisplay?.(row)}
                    timelinessEmphasis={displayExtras?.getTimelinessEmphasis?.(row)}
                    statusDisplayAsText={displayExtras?.statusDisplayAsText}
                    footerActionsPreset={displayExtras?.footerActionsPreset}
                    hideAssignedOuColumn={hideAssignedOuColumn}
                    hideStatusColumn={displayExtras?.hideStatusColumn}
                    categoryColumnLabel={displayExtras?.categoryColumnLabel}
                    categoryDisplay={displayExtras?.getCategoryDisplay?.(row) ?? null}
                    assignedOuLozengeStyle={displayExtras?.getAssignedOuLozengeStyle?.(row) ?? null}
                    funnelProgress={funnelProgress}
                    layout={listCardContext?.layout ?? "mobile"}
                    listSelection={listCardContext?.selection}
                    isPanelActive={listCardContext?.isPanelActive}
                    footerControls={footerControls}
                    footerActionsRestricted={footerActionsRestricted}
                    scheduleClickDisabled={scheduleClickDisabled}
                    onCardClick={
                        listCardContext === undefined
                            ? undefined
                            : () => {
                                  listCardContext.openReadPanel();
                              }
                    }
                    onPhoneClick={() => {
                        h.onPhoneClick(row, footerBindings);
                    }}
                    onScheduleClick={
                        h.onScheduleClick === undefined
                            ? undefined
                            : (footerCorrelationId) => {
                                  h.onScheduleClick!(row, footerCorrelationId, footerBindings);
                              }
                    }
                    onDisposeClick={
                        h.onDisposeClick === undefined
                            ? undefined
                            : (footerCorrelationId) => {
                                  h.onDisposeClick!(row, footerCorrelationId, footerBindings);
                              }
                    }
                    onCreateClick={
                        h.onCreateClick === undefined
                            ? undefined
                            : (footerCorrelationId) => {
                                  h.onCreateClick!(row, footerCorrelationId, footerBindings);
                              }
                    }
                    onNotesClick={
                        h.onNotesClick === undefined
                            ? undefined
                            : (footerCorrelationId) => {
                                  h.onNotesClick!(row, footerCorrelationId, footerBindings);
                              }
                    }
                    onEditClick={
                        h.onEditClick === undefined
                            ? undefined
                            : (footerCorrelationId) => {
                                  h.onEditClick!(row, footerCorrelationId, footerBindings);
                              }
                    }
                    onLogCallClick={
                        h.onLogCallClick === undefined
                            ? undefined
                            : (footerCorrelationId) => {
                                  h.onLogCallClick!(row, footerCorrelationId, footerBindings);
                              }
                    }
                    onProfileClick={
                        h.onProfileClick === undefined
                            ? undefined
                            : (footerCorrelationId) => {
                                  h.onProfileClick!(row, footerCorrelationId, footerBindings);
                              }
                    }
                    onWhatsAppClick={
                        h.onWhatsAppClick === undefined
                            ? undefined
                            : (footerCorrelationId) => {
                                  h.onWhatsAppClick!(row, footerCorrelationId, footerBindings);
                              }
                    }
                    whatsAppActionDisabled={whatsAppActionDisabled}
                    footerActionsRenderer={footerActionsRenderer}
                />
            );
        },
        [footerBindings],
    );

    return {
        renderListCard,
        footerBindings,
    };
}

export function createOttopilotListView<T extends {id: Uuid}>(
    id: Uuid,
    renderListCard: (row: T, context?: OttopilotListCardRenderContext) => ReactElement,
    options?: Pick<Extract<OttopilotView<T>, {viewType: typeof ViewType.list}>, "onListCardClick">,
): Extract<OttopilotView<T>, {viewType: typeof ViewType.list}> {
    return {
        id,
        viewType: ViewType.list,
        cardRenderer: renderListCard,
        ...options,
    };
}
